import { useRouter } from '@tanstack/react-router'
import { useRef, useState, type JSX } from 'react'

import { AccountMenu } from './account-menu.tsx'
import { CategoryPanel } from './category-panel.tsx'
import {
  NavRow,
  RailListHeading,
  StoredPageList,
  rowClass,
  slugifyGroup
} from './console-nav-row.tsx'
import { Icon } from '../design-system/icon.tsx'
import { Link } from '../design-system/link.tsx'
import { cn } from '../lib/cn.ts'
import { isApplePlatform, searchShortcutLabel } from '../lib/page-search.ts'
import {
  normalizePath,
  type InternalNavSection,
  type InternalPage
} from '../lib/pages.ts'

/** The quiet control style on the navy rail: the menu button. */
export const SIDEBAR_CONTROL_CLASS =
  'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground'

/** What `AppNav` needs from the layout that owns the rail's state. */
export interface AppNavProps {
  pathname: string
  mobile?: boolean
  onSelect: () => void
  /** Every section this reader may open. */
  sections: readonly InternalNavSection[]
  /** The category whose panel is open, or null when the panel is closed. */
  openGroup: string | null
  onToggleGroup: (group: string) => void
  /** Closes the category panel without opening another category. */
  onCloseGroup: () => void
  /** Starred pages the reader may open, in star order. */
  starredPages: readonly InternalPage[]
  /** Recent pages the reader may open, newest first. */
  recentPages: readonly InternalPage[]
  onMoveSection: (group: string, direction: -1 | 1) => void
  /** Whether the rail shows section headings and move buttons. */
  customizing: boolean
  onCustomizingChange: (customizing: boolean) => void
  /** Why the last rail change was not saved, or null. */
  saveError: string | null
  accessStatus: 'loading' | 'error' | 'ready'
  /** Opens page search over the console. */
  onOpenSearch: () => void
  /** True once saved settings have loaded. Stars stay hidden until then. */
  starsEnabled: boolean
  /** Stars or unstars one report. The caller saves and toasts. */
  onToggleStar: (pageId: string) => void
  /** True while the mobile sheet is open, so Escape belongs to the sheet. */
  sheetOpen: boolean
  /** Whether the reader collapsed Recent. Saved on their settings. */
  recentCollapsed: boolean
  /** Collapses or opens Recent. The caller saves the choice. */
  onToggleRecent: () => void
}

/**
 * The page list, in desktop and mobile layouts alike.
 *
 * Search is the first control, above the title and Home. Starred and Recent
 * sit above the categories, in both modes. Recent collapses from its heading. A category opens its reports
 * beside the rail. On a narrow screen it replaces the sheet's list instead.
 * Customize mode keeps the section headings and the move buttons. It does
 * not list the reports.
 *
 * @param props - Nav props; see `AppNavProps`.
 * @returns The rendered page list.
 */
export function AppNav({
  pathname,
  mobile = false,
  onSelect,
  sections,
  openGroup,
  onToggleGroup,
  onCloseGroup,
  starredPages,
  recentPages,
  onMoveSection,
  customizing,
  onCustomizingChange,
  saveError,
  accessStatus,
  onOpenSearch,
  starsEnabled,
  onToggleStar,
  sheetOpen,
  recentCollapsed,
  onToggleRecent
}: AppNavProps): JSX.Element {
  const router = useRouter()
  const [moveNotice, setMoveNotice] = useState('')
  const navRef = useRef<HTMLElement>(null)
  const prefix = mobile ? 'mobile-' : ''
  const activePath = normalizePath(pathname)
  const shownSections = accessStatus === 'ready' ? sections : []
  function navigate(path: string): void {
    onSelect()
    onCloseGroup()
    router.history.push(path)
  }

  const storedPages = (
    <>
      <StoredPageList
        pages={starredPages}
        listId="nav-starred"
        heading="Starred"
        prefix={prefix}
        activePath={activePath}
        onNavigate={navigate}
      />
      <StoredPageList
        pages={recentPages}
        listId="nav-recent"
        heading="Recent"
        prefix={prefix}
        activePath={activePath}
        onNavigate={navigate}
        collapsed={recentCollapsed}
        onToggleCollapsed={onToggleRecent}
      />
      {starredPages.length > 0 || recentPages.length > 0 ? (
        // The top edge only. The all-sides rule width also paints the other edges.
        <div
          data-testid={`${prefix}nav-stored-divider`}
          aria-hidden="true"
          className="border-sidebar-border shrink-0 border-t-(length:--rule-width)"
        />
      ) : null}
    </>
  )

  const apple = isApplePlatform()

  return (
    <nav
      ref={navRef}
      data-testid={`${prefix}nav-landmark`}
      aria-label="Internal apps"
      // No `relative` here. An absolute category panel must use the
      // screen-height rail box as its containing block, not this nav.
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="shrink-0 px-3 pt-3">
        {accessStatus === 'ready' ? (
          // The sheet's close control overlaps this row. Reserve space outside its border.
          <button
            type="button"
            data-testid={`${prefix}nav-search`}
            className={cn(
              'border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent mb-2 flex w-full cursor-pointer items-center gap-2 border border-(length:--rule-width) px-3 py-2 text-left text-sm',
              mobile && 'mr-10 w-auto'
            )}
            onClick={onOpenSearch}
            aria-keyshortcuts={apple ? 'Meta+K' : 'Control+K'}
          >
            <Icon name="search" className="size-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              Search pages, merchants, figures…
            </span>
            <span aria-hidden="true" className="shrink-0 text-xs">
              {searchShortcutLabel(apple)}
            </span>
          </button>
        ) : null}
        {/* Without search, this row still shares the sheet close control's top right. */}
        <div
          className={cn(
            'flex items-center justify-between gap-2 pt-2',
            mobile && accessStatus !== 'ready' && 'pr-8'
          )}
        >
          <p
            data-testid={`${prefix}app-title`}
            className="text-sidebar-foreground type-section-title font-display px-3 whitespace-nowrap"
          >
            BlytzPay Internal
          </p>
          {/* One element in both modes, so focus stays on it when it flips.
              Small in both, so the title beside it never wraps. */}
          <button
            type="button"
            data-testid={`${prefix}nav-customize`}
            aria-label={customizing ? undefined : 'Customize pages'}
            disabled={accessStatus !== 'ready'}
            className={cn(
              rowClass(false),
              'shrink-0',
              customizing ? 'px-2 py-1 text-xs font-medium' : 'p-2'
            )}
            onClick={() => onCustomizingChange(!customizing)}
          >
            {customizing ? (
              'Done'
            ) : (
              <Icon name="sliders-horizontal" className="size-4" />
            )}
          </button>
        </div>
        <div
          data-testid={`${prefix}nav-divider`}
          className="bg-sidebar-border my-2 h-px"
        />
        {accessStatus === 'ready' ? (
          <Link
            data-testid={`${prefix}nav-home`}
            href="/"
            onNavigate={() => navigate('/')}
            className={cn(rowClass(activePath === '/'), 'mb-1')}
            aria-current={activePath === '/' ? 'page' : undefined}
          >
            <Icon name="home" className="size-4 shrink-0" />
            Home
          </Link>
        ) : null}
        {customizing ? (
          <p
            data-testid={`${prefix}nav-customize-hint`}
            className="text-sidebar-foreground px-3 pb-2 text-xs"
          >
            Move sections up or down.
          </p>
        ) : null}
        {saveError ? (
          <p
            role="status"
            data-testid={`${prefix}nav-save-error`}
            className="text-sidebar-foreground px-3 pb-2 text-xs"
          >
            {saveError}
          </p>
        ) : null}
        <p
          role="status"
          data-testid={`${prefix}nav-move-status`}
          className="sr-only"
        >
          {moveNotice}
        </p>
      </div>
      <div
        data-testid={`${prefix}nav-pages`}
        className="scrollbar-sidebar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3"
      >
        {accessStatus !== 'ready' ? (
          <p
            data-testid={`${prefix}nav-access-${accessStatus}`}
            className="text-sidebar-foreground px-3 py-2 text-sm"
          >
            {accessStatus === 'loading'
              ? 'Checking page access…'
              : 'Page access could not be checked.'}
          </p>
        ) : null}
        {customizing ? (
          <>
            {accessStatus === 'ready' ? storedPages : null}
            {shownSections.map((section, sectionIndex) => {
              const slug = slugifyGroup(section.group)
              return (
                <div
                  key={section.group}
                  data-testid={`${prefix}nav-section-container-${slug}`}
                  className="flex flex-col gap-1"
                >
                  <div className="mt-2 flex items-center gap-1">
                    <p
                      data-testid={`${prefix}nav-section-${slug}`}
                      className="text-sidebar-foreground min-w-0 flex-1 px-3 py-2 text-xs font-semibold"
                    >
                      {section.group}
                    </p>
                    <span className="flex shrink-0 items-center">
                      {([-1, 1] as const).map(direction => (
                        <button
                          key={direction}
                          type="button"
                          data-testid={`${prefix}nav-move-${slug}-${direction === -1 ? 'up' : 'down'}`}
                          aria-label={`Move ${section.group} ${direction === -1 ? 'up' : 'down'}`}
                          disabled={
                            direction === -1
                              ? sectionIndex === 0
                              : sectionIndex === shownSections.length - 1
                          }
                          className={cn(
                            rowClass(false),
                            'p-2 disabled:bg-sunk disabled:text-muted-foreground disabled:opacity-100'
                          )}
                          onClick={event => {
                            const button = event.currentTarget
                            onMoveSection(section.group, direction)
                            setMoveNotice(
                              `${section.group}, position ${sectionIndex + direction + 1} of ${sections.length}`
                            )
                            requestAnimationFrame(() => {
                              if (button.disabled) {
                                button.parentElement
                                  ?.querySelector<HTMLButtonElement>(
                                    'button:not(:disabled)'
                                  )
                                  ?.focus()
                              } else {
                                button.focus()
                              }
                            })
                          }}
                        >
                          <Icon
                            name={
                              direction === -1 ? 'chevron-up' : 'chevron-down'
                            }
                            className="size-4"
                          />
                        </button>
                      ))}
                    </span>
                  </div>
                </div>
              )
            })}
          </>
        ) : (
          <CategoryPanel
            sections={shownSections}
            openGroup={openGroup}
            mobile={mobile}
            activePath={activePath}
            onToggleGroup={onToggleGroup}
            onCloseGroup={onCloseGroup}
            onNavigate={navigate}
            starsEnabled={starsEnabled}
            starredIds={new Set(starredPages.map(page => page.id))}
            onToggleStar={onToggleStar}
            navRef={navRef}
            sheetOpen={sheetOpen}
          >
            {accessStatus === 'ready' ? storedPages : null}
          </CategoryPanel>
        )}
      </div>
    </nav>
  )
}

/**
 * The rail's fixed Tools foot: a Tools heading, the tool links, then one
 * account row. The account row opens the zone, the theme and sign out.
 *
 * Outside the scrolling page list, so a long report list cannot scroll the
 * tools away. The Tools heading collapses the tool links and keeps the
 * account row. Customize mode does not hide a tool link.
 *
 * @param props - Footer props.
 * @param props.mobile - True inside the sheet, false on the desktop rail.
 * @param props.onSelect - Closes the mobile sheet before a navigation or a sign-out runs.
 * @param props.pathname - The current path, to mark the active tool.
 * @param props.pages - The tool links to show. The caller chooses them with `footPages`.
 * @param props.accountOpen - Whether this foot's account menu is open.
 * @param props.onAccountOpenChange - Told when this foot's account menu opens or closes.
 * @param props.collapsed - Whether the reader collapsed the tool links.
 * @param props.onToggleCollapsed - Collapses or opens the tool links. The caller saves the choice.
 * @returns The rendered footer.
 */
export function SidebarFooter({
  mobile = false,
  onSelect,
  pathname,
  pages,
  accountOpen,
  onAccountOpenChange,
  collapsed,
  onToggleCollapsed
}: {
  mobile?: boolean
  onSelect: () => void
  pathname: string
  pages: readonly InternalPage[]
  accountOpen?: boolean
  onAccountOpenChange?: (open: boolean) => void
  collapsed: boolean
  onToggleCollapsed: () => void
}): JSX.Element {
  const router = useRouter()
  const prefix = mobile ? 'mobile-' : ''
  const activePath = normalizePath(pathname)

  return (
    <div className="shrink-0 p-3 pt-0">
      <div className="bg-sidebar-border my-2 h-px" />
      {pages.length > 0 ? (
        <nav
          data-testid={`${prefix}nav-pinned`}
          aria-labelledby={`${prefix}nav-tools-heading`}
          className="mb-1 flex flex-col gap-1"
        >
          <RailListHeading
            id={`${prefix}nav-tools-heading`}
            label="Tools"
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
          />
          {(collapsed ? [] : pages).map(page => (
            <NavRow
              key={page.id}
              page={page}
              icon={page.icon ?? 'app-window'}
              active={activePath === normalizePath(page.path)}
              testId={`${prefix}nav-${page.id}`}
              onNavigate={() => {
                onSelect()
                router.history.push(page.path)
              }}
            />
          ))}
        </nav>
      ) : null}
      <AccountMenu
        data-testid={mobile ? 'mobile-session' : 'session'}
        onSelect={onSelect}
        open={accountOpen}
        onOpenChange={onAccountOpenChange}
        modal={mobile}
      />
    </div>
  )
}
